import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { Box, Button, Chip, CircularProgress, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState, type ReactNode } from "react";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { colors } from "../ui/colors";
import { useGetEventDeliveryLogDetail } from "./hooks/useGetEventDeliveryLogDetail";
import { useResendEventDeliveryLog } from "./hooks/useResendEventDeliveryLog";

const formatTimestamp = (value?: string | null) => {
  if (!value) return "—";
  const parsed = DateTime.fromISO(value);
  if (!parsed.isValid) return value;
  return parsed.toLocal().toFormat("MMM d, yyyy • HH:mm:ss");
};

const SectionHeaderWithCopy = ({ title, value }: { title: string; value: unknown }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore fallback errors
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{title}</Typography>
      <Tooltip title={copied ? "Copied!" : `Copy ${title.toLowerCase()}`}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            p: 0.5,
            color: copied ? "#16a34a" : colors.gray[500],
            bgcolor: copied ? "rgba(34,197,94,0.1)" : "transparent",
            "&:hover": { bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.05)" },
          }}
        >
          {copied ? <CheckOutlinedIcon sx={{ fontSize: 16 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const JsonBlock = ({ value }: { value: unknown }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      borderRadius: "12px",
      bgcolor: "#0f172a",
      color: "#e2e8f0",
      fontSize: 12,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      overflow: "auto",
      maxHeight: 280,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {JSON.stringify(value, null, 2)}
  </Box>
);

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.gray[400], textTransform: "uppercase", letterSpacing: "0.4px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.gray.base, mt: 0.25, wordBreak: "break-all" }}>{value || "—"}</Typography>
  </Box>
);

export const EventDeliveryLogDetails = ({ logUid }: { logUid: string | null }) => {
  const { showSnackbar } = useGlobalSnackbar();
  const { data, isLoading, isError } = useGetEventDeliveryLogDetail(logUid);
  const resendLog = useResendEventDeliveryLog();
  const log = data?.log;
  const event = data?.event;
  const canResend = log?.source === "WEBHOOK" && Boolean(event);

  const handleResend = async () => {
    if (!logUid) return;
    try {
      const result = await resendLog.mutateAsync(logUid);
      const status = result?.log?.http_status;
      if (status === 200) {
        showSnackbar("Webhook resent. The receiver responded HTTP 200.", "success");
      } else {
        showSnackbar(result?.log?.error || `Webhook resent. The receiver responded HTTP ${status ?? "error"}.`, "warning");
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || "Could not resend this webhook.", "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: colors.orange.base }} />
        </Box>
      ) : isError || !log ? (
        <Typography sx={{ color: colors.gray[500], fontWeight: 600 }}>Could not load this log.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip size="small" label={log.source} sx={{ fontWeight: 800 }} />
              {log.http_status != null ? <Chip size="small" label={`HTTP ${log.http_status}`} sx={{ fontWeight: 700 }} /> : null}
            </Box>
            {canResend ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={resendLog.isPending ? <CircularProgress size={14} color="inherit" /> : <ReplayOutlinedIcon />}
                disabled={resendLog.isPending}
                onClick={handleResend}
                sx={{ fontWeight: 800, textTransform: "none", borderRadius: "10px" }}
              >
                Resend
              </Button>
            ) : null}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <Field label="Log ID" value={log.uid} />
            <Field label="Timestamp" value={formatTimestamp(log.timestamp)} />
            <Field label="Attempt" value={String(log.attempt_number)} />
            <Field label="Duration" value={log.duration_ms != null ? `${log.duration_ms}ms` : "—"} />
            {log.item_count != null ? <Field label="Items returned" value={String(log.item_count)} /> : null}
            {log.error ? <Field label="Error" value={log.error} /> : null}
          </Box>

          {log.meta ? (
            <Box>
              <SectionHeaderWithCopy title="Metadata" value={log.meta} />
              <JsonBlock value={log.meta} />
            </Box>
          ) : null}

          {event ? (
            <>
              <Divider />
              <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Related event</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <Field label="Event ID" value={event.uid} />
                <Field label="Type" value={event.event_type} />
                <Field label="Entity" value={`${event.entity_type} • ${event.entity_id}`} />
                <Field label="Delivery status" value={event.delivery_status} />
                <Field label="Retries" value={String(event.retries)} />
                <Field label="Delivered at" value={formatTimestamp(event.delivered_at)} />
              </Box>
              <Box>
                <SectionHeaderWithCopy title="Payload" value={event.payload} />
                <JsonBlock value={event.payload} />
              </Box>
            </>
          ) : (
            <Typography sx={{ color: colors.gray[400], fontSize: 13, fontWeight: 600 }}>
              This log is not linked to an integration event payload.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export const EventDeliveryLogDetailDialog = EventDeliveryLogDetails;
